import React, { useState, useEffect, useRef } from 'react';
import { FilePdfOutlined } from '@ant-design/icons';
import { Table, Row, Col, Breadcrumb, Button, Typography, Select, Form, notification, Tooltip } from 'antd';
import {
  intialPaginationState,
  showingPageTotalRange,
  formItemLayout,
  tailFormItemLayout,
  unexpectedError,
  PictureCell,
} from '../../common_functions';
import { analysisCategory } from '../../config';
const CategoryAnalysis = props => {
  const { roleType, prints_negatives_obj, collections_permissions, analysisReport, getItems } = props;
  const formRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pagination, setPagination] = useState(intialPaginationState);
  const [loading, setLoading] = useState(false);
  const [collections, setCollections] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState({});

  const columns = [
    {
      title: 'Print Object ID',
      dataIndex: 'object_id',
      key: 'object_id',
      render: text => <>{text.id}</>,
    },
    {
      title: 'Collection Name',
      dataIndex: 'object_id',
      key: 'object_id',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text.collections.name}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'ID',
      dataIndex: 'acq_id',
      key: 'acq_id',
    },
    {
      title: 'Common Title',
      dataIndex: 'common_title',
      key: 'common_title',
    },
    {
      title: 'Thumbnail',
      dataIndex: 'negative_thumbnail',
      key: 'negative_thumbnail',
      render: (text, record) => {
        let thumbnail = record?.object_id?.print?.negative_id?.thumbnail;
        return thumbnail ? <PictureCell dataIndexValue={thumbnail} getFile={props.getFile} width={50} /> : <span>-</span>;
      },
    },
    {
      title: 'Object',
      dataIndex: 'object',
      key: 'object',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Print',
      dataIndex: 'print',
      key: 'print',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Physical Characteristics',
      dataIndex: 'phy_char',
      key: 'phy_char',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'XRF',
      dataIndex: 'xrf',
      key: 'xrf',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Micrometer',
      dataIndex: 'micrometer',
      key: 'micrometer',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Glossmeter',
      dataIndex: 'glossmeter',
      key: 'glossmeter',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Photospectrometer',
      dataIndex: 'photo_spectro_meter',
      key: 'photo_spectro_meter',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Image Description',
      dataIndex: 'image_description',
      key: 'image_description',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Mount Characteristics',
      dataIndex: 'print_mount_conditions',
      key: 'print_mount_conditions',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    // {
    //   title: 'Mount Conditions',
    //   dataIndex: 'mount_conditions',
    //   key: 'mount_conditions',
    //   render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    // },
    // {
    //   title: 'Mount Characteristics',
    //   dataIndex: 'mount_characteristics',
    //   key: 'mount_characteristics',
    //   render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    // },
    {
      title: 'Basic Photos',
      dataIndex: 'basic_photos',
      key: 'basic_photos',
      render: text => <span style={{ textTransform: 'capitalize' }}>{text == 1 ? 'Complete' : 'Incomplete'}</span>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <span>
          <Tooltip placement="top" title="Generate Pdf">
            <FilePdfOutlined onClick={e => generatePDF(record.object_id.id)} />
          </Tooltip>
        </span>
      ),
    },
  ];
  const generatePDF = async (id = null) => {
    const formValues = formRef.current.getFieldsValue();
    const errors = [];
    // Validate required fields
    const requiredFields = ['collections', 'analysis_category'];
    requiredFields.forEach(field => {
      if (!formValues[field]) {
        errors.push({
          name: field,
          errors: [`${field.replace('_', ' ')} is required!`],
        });
      }
    });

    if (errors.length === 0) {
      try {
        setLoading(true);
        // Generate PDF based on conditions
        const resultArray = [].concat(...Object.values(selectedRowKeys));
        if (resultArray.length > 10) {
          notification.info({
            message: 'Please select 10 records or fewer!',
            duration: 2,
          });
        } else if (id || resultArray.length > 0) {
          const requestData = id ? { ...formValues, object_id: id } : { ...formValues, object_ids: resultArray };
          await analysisReport(requestData);
          notification['success']({
            message: 'Report has been generated successfully!',
            duration: 2,
          });
          if (!id) {
            setSelectedRowKeys({});
          }
        } else {
          notification.info({
            message: 'No data has been selected for PDF generation!',
            duration: 2,
          });
        }
      } catch (error) {
        notification['error']({
          message: 'An error occurred during the report generation process!',
          duration: 2,
        });
      } finally {
        setLoading(false);
      }
    } else {
      formRef.current.setFieldsValue({}); // Clear previous errors
      formRef.current.setFields(errors);
    }
  };
  //Get collection data
  useEffect(() => {
    (async () => {
    let { data: collData } = await getItems('collections', {
      // fields: ['id', 'title', 'name'],
      fields: ['id', 'name'],
      sort: '-id',
      limit: -1,
      filters: {
        associated_to: {
          _in: ['print', 'both'],
        },
      },
    });
    setCollections(collData);
  })();
  }, []);
  const getCollLocations = async (collectionID = null) => {
    try {
      setLoading(true);
      let filters = !!collectionID ? { collections: { _eq: collectionID } } : {};
      let { data: locations_data } = await getItems('location', {
        fields: ['id', 'name'],
        sort: '-id',
        limit: -1,
        filters: filters,
      });
      setLocations(locations_data);
    } catch (error) {
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };
  const getColData = async (pagination, filters = {}) => {
    setLoading(true);
    try {
      if (roleType === 'admin' || prints_negatives_obj.length > 0) {
        const response = await getItems('basic_print_reports', {
          fields: ['*,object_id.collections.name,object_id.id,object_id.print.negative_id.thumbnail'],
          sort: '-id',
          filters: filters,
          offset: pagination.pageSize * (pagination.current - 1),
          limit: pagination.pageSize,
          meta: 'filter_count',
        });
        pagination.total = response.meta.filter_count;
        setPagination(pagination);
        setReports(response.data);
      }
    } catch (error) {
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = async (pagination, page) => {
    setPagination(pagination);
    let filtersitems = handleFilter(formRef?.current?.getFieldsValue());
    await getColData(pagination, filtersitems);
  };
  const onFinish = async values => {
    setSelectedRowKeys({});
    let filtersitems = handleFilter(values);
    await getColData(pagination, filtersitems);
  };
  const handleFilter = values => {
    const { analysis_category, collections, locations, status } = values;
    const filtersitems = {};
    filtersitems.collections = { _eq: collections };
    //Admin
    if (roleType === 'admin') {
      filtersitems.collections = { _eq: collections };
      if (locations) filtersitems.object_id = { location: { id: { _eq: locations } } };
    } else if (roleType !== 'admin' && prints_negatives_obj.length > 0) {
      const roleFilters = [{ collections: { _in: collections_permissions.view } }, { collections: { _eq: collections } }];
      if (locations) {
        roleFilters.push({ object_id: { location: { id: { _eq: locations } } } }, { object_id: { _in: prints_negatives_obj } });
      }
      filtersitems['_and'] = roleFilters;
    }
    if (status) {
      const subfilterMapping = {
        // all: ['mount_characteristics', 'photo_spectro_meter', 'phy_char', 'xrf', 'micrometer', 'mount_condition', 'basic_photos'],
        all: ['print_mount_conditions', 'photo_spectro_meter', 'phy_char', 'xrf', 'micrometer', 'basic_photos'],
        physical_characteristics: 'phy_char',
        photospectrometer: 'photo_spectro_meter',
        // mount_characteristics: 'mount_characteristics',
        xrf: 'xrf',
        micrometer: 'micrometer',
        // mount_condition: 'mount_condition_characteristics',
        basic_photos: 'basic_photos',
        print_mount_conditions: 'print_mount_conditions',
      };
      const subfilterFields = analysis_category === 'all' ? subfilterMapping.all : [subfilterMapping[analysis_category]];
      filtersitems['_and'] = subfilterFields.map(field => ({
        [field]: { _eq: status },
      }));
    }
    return filtersitems;
  };
  const onSelectChange = newSelectedRowKeys => {
    setSelectedRowKeys(prevSelectedRowKeys => ({
      ...prevSelectedRowKeys,
      [pagination.current]: newSelectedRowKeys,
    }));
  };
  // Row selection object
  const rowSelection = {
    selectedRowKeys: selectedRowKeys[pagination.current] || [],
    onChange: onSelectChange,
  };
  return (
    <Row className={!!loading ? 'disabledComponent' : ''}>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Reports", href: "/reports/reading-reports/category-reports" }, { title: "Category Reading Reports" }]} />
      </Col>
      <Col className="white-paper" span={24}>
        <div className="dashboard">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Reports
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end" style={{ display: 'flex' }}>
              <Button
                style={{
                  marginRight: '10px',
                }}
                icon={<FilePdfOutlined />}
                className="align-right"
                type="primary"
                size="small"
                onClick={e => generatePDF()}
              >
                Generate Pdf
              </Button>
            </Col>
          </Row>
          <Form ref={formRef} layout="vertical" onFinish={onFinish} {...formItemLayout} initialValues={{ layout: tailFormItemLayout }}>
            <Row gutter={24}>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item
                  name="collections"
                  label="Collections"
                  rules={[
                    {
                      required: true,
                      message: 'Collection is required!',
                    },
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select Collection"
                    optionFilterProp="children"
                    onChange={getCollLocations}
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  >
                    {collections.map(col => {
                      return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item
                  name="analysis_category"
                  label="Analysis Category"
                  rules={[
                    {
                      required: true,
                      message: 'Analysis Category is required!',
                    },
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select Analysis Category"
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  >
                    {/* <Select.Option key="" value="">All</Select.Option> */}
                    {analysisCategory.map(col => {
                      return <Select.Option key={col.key} value={col.key}>{col.value}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item name="status" label="Status">
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select status"
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  >
                    <Select.Option key="0" value="0">Incomplete</Select.Option>;<Select.Option key="1" value="1">Complete</Select.Option>;
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={5} md={5} lg={5}>
                <Form.Item name="locations" label="Locations">
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select Location"
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  >
                    {locations.map(col => {
                      return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={4} md={4} lg={4}>
                <Form.Item>
                  <Button type="primary" size="small" htmlType="submit" style={{ height: '42px', marginTop: '28px' }}>
                    Search
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <hr />
          <div className="artist-list">
            <Table
              scroll={{ x: 'max-content' }}
              columns={columns}
              bordered
              rowSelection={rowSelection}
              rowKey={record => record.object_id.id}
              dataSource={reports}
              pagination={reports.length > 0 ? { ...pagination, ...showingPageTotalRange } : false}
              loading={loading}
              onChange={handleTableChange}
            />
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default CategoryAnalysis;
